import { FileActionButton } from "../file-action/file-action-controls";
import { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";
import AppLoading from "../app-loading/app-loading";
import { exportPOSInventoryExcel } from "./POS-InventoryExcel";
import "primeicons/primeicons.css";

interface Product {
  name: string;
  [key: string]: number | string;
}

interface PosInventoryReportDTO {
  posId: number;
  posCode: string;
  productName: string;
  quantity: number;
  value: number;
}

interface PosSummaryDTO {
  posID: number;
  posCode: string;
  address1: string;
  address2?: string;
  landmark?: string;
  countryName: string;
  stateName: string;
  cityName: string;
  zipcode: string;
}

interface PosTotalDTO {
  posId: number;
  posCode: string;
  totalValue: number;
}

interface POSInventoryReportResponse {
  data: PosInventoryReportDTO[];
  totalRecords: number;
  page: number;
  pageSize: number;
  posTotals: PosTotalDTO[];
}

interface ProductDto {
  productId: number;
  productName: string;
}

const POSInventoryReport = () => {
  const [posList, setPosList] = useState<PosSummaryDTO[]>([]);
  const [selectedPOS, setSelectedPOS] = useState<number[]>([]);
  const [reportTime, setReportTime] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [posTotals, setPosTotals] = useState<PosTotalDTO[]>([]);
  const [allProducts, setAllProducts] = useState<ProductDto[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductDto[]>([]);
  const toast = useRef<Toast>(null);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<0 | 1 | -1>(1);

  useEffect(() => {
    const fetchPOSList = async () => {
      try {
        const data = await posService.getAllPOSBatch();
        setPosList(data);
      } catch (error) {
        console.error("Error fetching POS list:", error);
      }
    };
    fetchPOSList();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await posService.getAllPosProducts();
        setAllProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load products",
          life: 3000,
        });
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedPOS.length > 0) {
      fetchData();
      const now = new Date();
      const formatted =
        now.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " " +
        now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });

      setReportTime(formatted);
    } else {
      setProducts([]);
      setTotalRecords(0);
      setPosTotals([]);
    }
  }, [selectedPOS, selectedProducts, page, pageSize, sortField, sortOrder]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response: POSInventoryReportResponse =
        await posService.getPOSInventoryReport({
          PosIds: selectedPOS,
          ProductIds: selectedProducts.map((p) => p.productId),
          Page: page,
          PageSize: pageSize,
          SortBy: sortField,
          SortDescending: sortOrder === -1,
        });
      transformData(response.data);
      setTotalRecords(response.totalRecords);
      setPosTotals(response.posTotals || []);
    } catch (error) {
      console.error("Error fetching POS inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const transformData = (apiData: PosInventoryReportDTO[]) => {
    const productMap: Record<string, Product> = {};

    apiData.forEach((item) => {
      if (!productMap[item.productName]) {
        productMap[item.productName] = { name: item.productName };
      }

      const posName = `POS${item.posId}`;
      productMap[item.productName][`${posName}_Qty`] = item.quantity;
      productMap[item.productName][`${posName}_Val`] = item.value;
    });

    selectedPOS.forEach((posId) => {
      const posName = `POS${posId}`;
      Object.values(productMap).forEach((product) => {
        if (!product[`${posName}_Qty`]) {
          product[`${posName}_Qty`] = 0;
          product[`${posName}_Val`] = 0;
        }
      });
    });

    setProducts(Object.values(productMap));
  };

  const productsWithTotals = products.map((p) => {
    let totalQty = 0;
    let totalVal = 0;

    selectedPOS.forEach((posId) => {
      const posName = `POS${posId}`;
      totalQty += Number(p[`${posName}_Qty`] || 0);
      totalVal += Number(p[`${posName}_Val`] || 0);
    });

    return { ...p, Total_Qty: totalQty, Total_Val: totalVal };
  });

  const formatNumber = (value: number) =>
    value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const quantityBodyTemplate = (rowData: Product, column: any) => {
    const field = column.field;
    return formatNumber(Number(rowData[field]));
  };

  const valueBodyTemplate = (rowData: Product, column: any) => {
    const field = column.field;
    const value = Number(rowData[field]);
    return <span className="value-cell">₹{formatNumber(value)}</span>;
  };

  const valueHeaderTemplate = (label: string) => {
    return <span className="value-header">{label}</span>;
  };

  const grandTotal = posTotals.reduce((sum, pos) => sum + pos.totalValue, 0);

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Product Name" rowSpan={2} field="name" sortable />
        {selectedPOS.map((posId) => {
          const pos = posList.find((p) => p.posID === posId);
          return (
            <Column
              header={pos?.posCode || `POS${posId}`}
              colSpan={2}
              key={`${posId}_header`}
            />
          );
        })}
        <Column header="Total" colSpan={2} />
      </Row>
      <Row>
        {selectedPOS.map((posId) => [
          <Column
            header="Quantity"
            field={`POS${posId}_Qty`}
            sortable
            key={`qty-${posId}`}
          />,
          <Column
            header={valueHeaderTemplate("Value")}
            field={`POS${posId}_Val`}
            sortable
            key={`val-${posId}`}
          />,
        ])}
        <Column header="Quantity" field="Total_Qty" sortable key="total_qty_head" />
        <Column
          header={valueHeaderTemplate("Value")}
          field="Total_Val"
          sortable
          key="total_val_head"
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <Card title="FG Inventory Value Report">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4 align-items-center">
          <div className="flex flex-wrap gap-3">
            {posList.map((pos) => (
              <div key={pos.posID} className="flex align-items-center gap-2">
                <Checkbox
                  inputId={`pos-${pos.posID}`}
                  value={pos.posID}
                  onChange={(e) => {
                    const val = e.value;
                    setSelectedPOS((prev) =>
                      prev.includes(val)
                        ? prev.filter((p) => p !== val)
                        : [...prev, val]
                    );
                  }}
                  checked={selectedPOS.includes(pos.posID)}
                />
                <label htmlFor={`pos-${pos.posID}`}>{pos.posCode}</label>
              </div>
            ))}
          </div>

          <div className="min-w-[250px]">
            <MultiSelect
              value={selectedProducts}
              options={allProducts}
              onChange={(e) => setSelectedProducts(e.value)}
              optionLabel="productName"
              placeholder="Select Products"
              display="chip"
              className="w-full"
              filter
              filterBy="productName"
              filterPlaceholder="Search products"
            />
          </div>
        </div>

        <div className="flex justify-content-between align-items-center">
          {selectedPOS.length > 0 ? (
            <>
              <div className="flex flex-column gap-1" style={{ alignItems: "flex-start" }}>
                <Message
                  severity="info"
                  text={`Report generated: ${reportTime} & Note: No tax included`}
                  className="w-full"
                />
                
              </div>
              <FileActionButton action="export"
                label="Export Report"
                icon="pi pi-file-excel"
                className="p-button-success"
                onClick={() =>
                  exportPOSInventoryExcel(
                    selectedPOS,
                    selectedProducts.map((p) => p.productId)
                  )
                }
              />
            </>
          ) : (
            <span></span>
          )}
        </div>

        {selectedPOS.length > 0 && posTotals.length > 0 && (
          <div className="inline-block">
            <DataTable
              value={[...posTotals].sort((a, b) =>
                a.posCode.localeCompare(b.posCode, "en", { numeric: true })
              )}
              showGridlines
              stripedRows
              size="small"
              className="p-datatable-sm"
              style={{ width: "auto" }}
            >
              <Column
                field="posCode"
                header="POS Code"
                footer="Grand Total"
                style={{ fontWeight: "bold" }}
              />
              <Column
                field="totalValue"
                header="Total Inventory Value"
                body={(rowData) => `₹${formatNumber(rowData.totalValue)}`}
                footer={`₹${formatNumber(grandTotal)}`}
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              />
            </DataTable>
          </div>
        )}
        <Divider />

        {loading ? (
          <AppLoading />
        ) : selectedPOS.length > 0 ? (
          <>
            <DataTable
              value={productsWithTotals}
              headerColumnGroup={headerGroup}
              showGridlines
              className="p-datatable-sm"
              loading={loading}
              paginator
              rows={pageSize}
              first={(page - 1) * pageSize}
              totalRecords={totalRecords}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              stripedRows
              emptyMessage="No inventory data found."
              lazy
              sortField={sortField}
              sortOrder={sortOrder}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              onPage={(e) => {
                setPage((e.page ?? 0) + 1);
                setPageSize(e.rows ?? pageSize);
              }}
              onSort={(e) => {
                if (!e.sortOrder) {
                  setSortField("name");
                  setSortOrder(1);
                } else {
                  setSortField(e.sortField);
                  setSortOrder(e.sortOrder as 1 | -1);
                }
              }}
            >
              <Column
                field="name"
                header="Product Name"
                sortable
                style={{ minWidth: "120px" }}
              />
              {selectedPOS.map((posId) => [
                <Column
                  field={`POS${posId}_Qty`}
                  key={`POS${posId}_Qty`}
                  sortable
                  body={quantityBodyTemplate}
                  style={{ textAlign: "center", minWidth: "30px" }}
                />,
                <Column
                  field={`POS${posId}_Val`}
                  key={`POS${posId}_Val`}
                  sortable
                  body={valueBodyTemplate}
                  style={{ textAlign: "center", minWidth: "80px" }}
                />,
              ])}
              <Column
                field="Total_Qty"
                body={quantityBodyTemplate}
                sortable
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  minWidth: "30px",
                }}
              />
              <Column
                field="Total_Val"
                body={valueBodyTemplate}
                sortable
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  minWidth: "80px",
                }}
              />
            </DataTable>
          </>
        ) : (
          <div className="p-4 text-center">
            <i className="pi pi-info-circle mr-2" />
            Please select at least one POS location to view the report
          </div>
        )}
      </Card>
    </div>
  );
};

export default POSInventoryReport;
